import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Button, Card, MenuItem, Grid, Paper, Select, InputLabel, FormControl, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import axios from 'axios';
import API_ENDPOINTS from '../config/apiConfig';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

function BirthdayPostcards() {
  const [postcardCost, setPostcardCost] = useState('');
  const [milestoneAge, setMilestoneAge] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filteredBirthdays, setFilteredBirthdays] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [milestoneAges, setMilestoneAges] = useState([]);
  const [newMilestoneAge, setNewMilestoneAge] = useState('');
  const [loading, setLoading] = useState(false);

  const columnDefs = [
    { headerName: 'Name', field: 'fullName', sortable: true, filter: true, flex: 1 },
    { headerName: 'Date of Birth', field: 'formattedBirthday', sortable: true, filter: true, flex: 1 },
    { headerName: 'HighLevel ID', field: 'highlevelId', sortable: true, filter: true, flex: 1 },
    { headerName: 'Tags', field: 'tags', sortable: true, filter: true, flex: 1,
      valueFormatter: (params) => params.value ? params.value.join(', ') : '' },
    { headerName: 'Recurring VBM', field: 'Recurring_VBM', sortable: true, filter: true, flex: 1 },
    { headerName: 'City', field: 'City', sortable: true, filter: true, flex: 1 },
    { headerName: 'State', field: 'State', sortable: true, filter: true, flex: 1 },
  ];

  const defaultColDef = {
    resizable: true,
    minWidth: 150,
  };

  const fetchMilestoneAges = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.milestoneAges}`);
      // Sort milestone ages numerically
      const sortedAges = response.data.map((item) => item.age).sort((a, b) => a - b);
      setMilestoneAges(sortedAges);
    } catch (error) {
      console.error('Error fetching milestone ages:', error);
    }
  };

  const handleAddMilestoneAge = async () => {
    if (newMilestoneAge && !milestoneAges.includes(Number(newMilestoneAge))) {
      try {
        await axios.post(`${API_ENDPOINTS.milestones}`, { age: Number(newMilestoneAge) });
        const updatedAges = [...milestoneAges, Number(newMilestoneAge)].sort((a, b) => a - b);
        setMilestoneAges(updatedAges);
        setNewMilestoneAge('');
      } catch (error) {
        console.error('Error adding milestone age:', error);
      }
    }
  };

  const fetchFilteredBirthdays = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth && selectedMonth !== 'All Year') {
        params.month = selectedMonth;
      }
      if (milestoneAge && milestoneAge.length > 0) {
        params.turningAge = milestoneAge.join(',');
      }

      const response = await axios.get(API_ENDPOINTS.contacts, { params });
      const data = response.data.map((entry) => ({
        fullName: `${entry.firstName} ${entry.lastName}`,
        formattedBirthday: `${entry.birthday.month.toString().padStart(2, '0')}/${entry.birthday.day.toString().padStart(2, '0')}/${entry.birthday.year}`,
        highlevelId: entry.highlevelId || '',
        tags: entry.tags || [],
        Recurring_VBM: entry.Recurring_VBM || '',
        City: entry.City || '',
        State: entry.State || ''
      }));

      setFilteredBirthdays(data);
      setTotalCost(data.length * parseFloat(postcardCost || 0));
    } catch (error) {
      console.error('Error fetching filtered birthdays:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setPostcardCost('');
    setMilestoneAge([]);
    setSelectedMonth('');
    setFilteredBirthdays([]);
    setTotalCost(0);
  };

  const exportToCSV = () => {
    if (filteredBirthdays.length === 0) return;

    const headers = [
      'Name',
      'Date of Birth',
      'HighLevel ID',
      'Tags',
      'Address',
      'Postal Code',
      'Gender',
      'Recurring VBM',
      'City',
      'State'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredBirthdays.map(row => [
        row.fullName,
        row.formattedBirthday,
        row.highlevelId,
        row.tags.join('; '),
        row.address1,
        row.postalCode,
        row.gender,
        row.Recurring_VBM,
        row.city,
        row.state
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'birthday_postcards.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchMilestoneAges();
  }, []);

  return (
    <Card
      sx={{
        padding: '24px',
        maxWidth: '100%',
        margin: '20px',
        height: 'calc(100vh - 60px)', // Fill viewport minus header
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent Card from overflowing
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
        Birthday Postcard Cost Estimator
      </Typography>

      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Postcard Cost (per card)"
            type="number"
            value={postcardCost}
            onChange={(e) => setPostcardCost(e.target.value)}
            fullWidth
            variant="outlined"
            InputProps={{ startAdornment: '$' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Select Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="All Year">All Year</MenuItem>
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="milestone-age-label">Milestone Age (Optional)</InputLabel>
            <Select
              labelId="milestone-age-label"
              multiple
              value={milestoneAge}
              onChange={(e) => setMilestoneAge(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Milestone Age (Optional)" />}
              renderValue={(selected) => selected.map(age => `Turning ${age}`).join(', ')}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {milestoneAges.map((age) => (
                <MenuItem key={age} value={age}>
                  <Checkbox checked={milestoneAge.indexOf(age) > -1} />
                  <ListItemText primary={`Turning ${age}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
        <TextField
          label="Add Milestone Age"
          type="number"
          value={newMilestoneAge}
          onChange={(e) => setNewMilestoneAge(e.target.value)}
          variant="outlined"
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddMilestoneAge}
          disabled={!newMilestoneAge}
          sx={{ minWidth: '150px' }}
        >
          Add Milestone Age
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <Button
          variant="contained"
          onClick={fetchFilteredBirthdays}
          disabled={loading}
          sx={{ minWidth: '150px' }}
        >
          {loading ? 'Loading...' : 'Apply Filter'}
        </Button>
        <Button
          variant="outlined"
          onClick={resetFilters}
          sx={{ minWidth: '150px' }}
        >
          Reset Filters
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={exportToCSV}
          disabled={filteredBirthdays.length === 0}
          startIcon={<FileDownloadIcon />}
          sx={{ minWidth: '150px' }}
        >
          Export CSV
        </Button>
      </Box>

      <Paper
        elevation={3}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          marginTop: '20px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Prevent Paper from overflowing
        }}
      >
        <AgGridReact
          rowData={filteredBirthdays}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={30}
          animateRows={true}
          className="ag-theme-alpine"
          domLayout="normal"
          style={{ flex: 1, minHeight: 0 }}
        />
      </Paper>

      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: '24px',
          left: '25%',
          backgroundColor: '#1976d2',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Typography variant="h7">
          Total Cost: ${totalCost.toFixed(2)}
        </Typography>
      </Paper>
    </Card>
  );
}

export default BirthdayPostcards;
